import { useState, useEffect, useCallback } from "react";
import { FranchiseTiles } from "@/components/franchise/FranchiseTiles";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Building2,
  Plus,
  Search,
  Edit,
  Trash2,
  FileText,
  Upload,
  Download,
  Eye,
  Users,
  Globe,
  Phone,
  Mail,
  MapPin,
  Calendar,
  DollarSign,
  Percent,
  Info,
  History,
  FileSignature,
  X,
  Loader2,
  ChevronRight,
  AlertCircle
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const COUNTRIES = ["India", "Australia", "United States", "United Kingdom", "Canada", "UAE", "Singapore", "Other"];
const STATUS_OPTIONS = ["Active", "Inactive", "Pending", "Terminated"];
// Document types now come from DB document_categories collection (no hardcoding)

// FOCO Model Constants
const FRANCHISE_TYPES = {
  "Sanskriti": { fee: 1100000, description: "2500+ Sq. Ft., 20-25 staff, 25-30 tables, 100-120 seating" },
  "Maaza": { fee: 900000, description: "1500-2000 Sq. Ft., 8-9 staff, 6-15 tables, 40-45 seating" },
  "Potoba": { fee: 700000, description: "Express format, smaller footprint" },
  "Peshwayee": { fee: 2500000, description: "Premium fine dining concept" }
};

const DEFAULT_WORKING_CAPITAL = 900000; // 9 Lakhs
const MONTHLY_SERVICE_CONTRACT = 10000;
import { resolveCurrency, formatMoney } from '../utils/money';

const REVENUE_SHARE_PERCENTAGE = 15;

const formatCurrency = (amount, country = "India", currencyCode = null) => {
  const meta = currencyCode ? resolveCurrency(currencyCode) : resolveCurrency({ country });
  return formatMoney(amount, meta, { digits: 0 });
};

const statusColors = {
  Active: "bg-green-500/20 text-emerald-700 border-green-500/30",
  Inactive: "bg-gray-500/20 text-stone-600 border-gray-500/30",
  Pending: "bg-yellow-500/20 text-amber-700 border-yellow-500/30",
  Terminated: "bg-red-500/20 text-red-700 border-red-500/30",
  Deleted: "bg-red-800/20 text-red-600 border-red-800/30"
};

export default function FranchiseManagement() {
  const { session } = useAuth();
  const [activeTab, setActiveTab] = useState("list");
  const [franchises, setFranchises] = useState([]);
  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState({ active: 0, pending: 0, terminated: 0, inactive: 0 });
  
  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCountry, setFilterCountry] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  
  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingFranchise, setEditingFranchise] = useState(null);
  const [formData, setFormData] = useState(getEmptyForm());
  
  // Detail view
  const [selectedFranchise, setSelectedFranchise] = useState(null);
  const [auditHistory, setAuditHistory] = useState([]);

  // iter228 — Franchise Agreement Date SA correction (legal master data)
  const [showAgreementCorrection, setShowAgreementCorrection] = useState(false);
  const [agrReasonCodes, setAgrReasonCodes] = useState([]);
  const [agrForm, setAgrForm] = useState({ new_date: '', reason_code: '', remarks: '', supporting_reference: '', confirmed: false });
  const [agrSubmitting, setAgrSubmitting] = useState(false);
  const [agrHistory, setAgrHistory] = useState([]);
  const resetAgreementCorrectionForm = () => {
    setAgrForm({
      new_date: selectedFranchise?.franchise_agreement_date || selectedFranchise?.agreement_start_date || '',
      reason_code: '', remarks: '', supporting_reference: '', confirmed: false,
    });
  };
  useEffect(() => {
    if (!showAgreementCorrection || agrReasonCodes.length) return;
    (async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL;
        const r = await fetch(`${API}/api/franchises/agreement-date/reason-codes`);
        const d = await r.json();
        setAgrReasonCodes(d?.codes || []);
      } catch (e) { /* silent */ }
    })();
  }, [showAgreementCorrection, agrReasonCodes.length]);
  const loadAgreementHistory = useCallback(async () => {
    if (!selectedFranchise?.franchise_code) return;
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const token = session?.token;
      const r = await fetch(`${API}/api/franchises/${selectedFranchise.franchise_code}/agreement-date/history`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      const d = await r.json();
      setAgrHistory(d?.history || []);
    } catch (e) { setAgrHistory([]); }
  }, [selectedFranchise, session]);
  const submitAgreementCorrection = async () => {
    if (!agrForm.new_date) { toast.error('Correct date is required'); return; }
    if (!agrForm.reason_code) { toast.error('Please pick a reason'); return; }
    if (agrForm.reason_code === 'OTHER' && !agrForm.remarks.trim()) { toast.error('Remarks are mandatory when reason is Other'); return; }
    if (!agrForm.confirmed) { toast.error('Please confirm the acknowledgement checkbox'); return; }
    setAgrSubmitting(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const token = session?.token;
      const r = await fetch(`${API}/api/franchises/${selectedFranchise.franchise_code}/correct-agreement-date`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          new_date: agrForm.new_date,
          reason_code: agrForm.reason_code,
          remarks: agrForm.remarks.trim(),
          supporting_reference: agrForm.supporting_reference.trim(),
          confirmed: true,
        }),
      });
      const d = await r.json();
      if (!r.ok || !d.success) throw new Error(d.detail || 'Correction failed');
      toast.success(`Franchise Agreement Date corrected (was ${d.old_value || '—'}, now ${d.new_value}).`);
      if ((d.warnings || []).length) {
        d.warnings.forEach(w => toast.warning(w));
      }
      setShowAgreementCorrection(false);
      const rr = await fetch(`${API}/api/franchises/${selectedFranchise.franchise_code}?token=${token}`);
      const dd = await rr.json();
      if (dd?.franchise) setSelectedFranchise(dd.franchise);
      loadAgreementHistory();
    } catch (e) {
      toast.error(e.message || 'Correction failed');
    } finally {
      setAgrSubmitting(false);
    }
  };
  
  // Document upload - uses new Document Management system
  const [showUpload, setShowUpload] = useState(false);
  const [uploadData, setUploadData] = useState({ document_type: "", document_name: "", notes: "", expiry_date: "" });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [franchiseDocs, setFranchiseDocs] = useState([]);
  const [docCategories, setDocCategories] = useState([]);

  function getEmptyForm() {
    return {
      franchise_code: "",
      franchise_name: "",
      legal_entity_name: "",
      country: "India",
      state: "",
      city: "",
      address: "",
      pincode: "",
      primary_contact_name: "",
      primary_contact_email: "",
      primary_contact_phone: "",
      directors: [{ name: "", email: "", phone: "", designation: "Director", address: "", pan: "" }],
      // FOCO Model fields
      franchise_type: "Sanskriti",
      franchise_fee: FRANCHISE_TYPES["Sanskriti"].fee,
      working_capital: DEFAULT_WORKING_CAPITAL,
      total_investment: 0,  // NEW: Total Investment for MG calculation
      setup_costs: {
        shop_security_deposit: 0,
        first_month_rent: 0,
        initial_salary_fund: 0,
        initial_grocery_cost: 0,
        staff_traveling_expense: 0
      },
      operations_start_date: "",
      agreement_start_date: "",
      agreement_end_date: "",
      revenue_share_start_date: "",  // NEW: When revenue share calculation starts
      revenue_share_percentage: REVENUE_SHARE_PERCENTAGE,
      franchise_owner_share_percentage: REVENUE_SHARE_PERCENTAGE,  // Renamed % (kept in sync with above)
      payout_model: "revenue_share",  // "revenue_share" | "profit_share" — overridable per franchise
      service_contract_fee: MONTHLY_SERVICE_CONTRACT,
      nominees: [],
      status: "Active",
      notes: "",
      gst_applicable: false,  // NEW: GST toggle for India locations
      mg_calculation_applicable: true,  // Default ON for new franchises (legacy behaviour)
      // Investment & Return Projection (Feb-2026) — Franchise Management is
      // the AUTHORITATIVE source of Actual Investment. Franchise Fee and
      // Working Capital are NEVER derived into this number.
      actual_investment_amount: 0,
      investment_currency: "",
      investment_date: "",
      additional_capital_introduced: 0,
      investment_working_capital: 0,
      other_capital_injection: 0,
      investment_notes: "",
      investment_verified_by: "",
      investment_verified_at: ""
    };
  }

  // Auto-calculate agreement end date (7 years from start)
  const calculateEndDate = (startDate) => {
    if (!startDate) return "";
    const start = new Date(startDate);
    const end = new Date(start);
    end.setFullYear(end.getFullYear() + 7);
    return end.toISOString().split('T')[0];
  };

  // Update franchise fee when type changes
  const handleFranchiseTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      franchise_type: type,
      franchise_fee: FRANCHISE_TYPES[type]?.fee || 0
    }));
  };

  // Update end date when start date changes
  const handleStartDateChange = (date) => {
    setFormData(prev => ({
      ...prev,
      operations_start_date: date,
      agreement_start_date: date,
      agreement_end_date: calculateEndDate(date)
    }));
  };

  const loadFranchises = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/franchises/list`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: session?.token,
          search: searchQuery || null,
          country: filterCountry && filterCountry !== "all" ? filterCountry : null,
          status: filterStatus && filterStatus !== "all" ? filterStatus : null
        })
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Failed to load franchises");
      }
      
      const data = await res.json();
      setFranchises(data.franchises || []);
      setCounts(data.counts || { active: 0, pending: 0, terminated: 0, inactive: 0 });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [session?.token, searchQuery, filterCountry, filterStatus]);

  useEffect(() => {
    if (session?.token) {
      loadFranchises();
      // Load document categories for the upload dialog
      fetch(`${API}/api/documents/categories/list`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: session?.token, level: "franchise" })
      }).then(r => r.json()).then(d => setDocCategories(d.categories || [])).catch(() => {});
    }
  }, [session?.token, loadFranchises]);

  // Load franchise documents from the new Document Management system
  const loadFranchiseDocs = async (franchiseCode) => {
    if (!session?.token || !franchiseCode) return;
    try {
      const res = await fetch(`${API}/api/documents/list`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: session?.token, franchise_code: franchiseCode, level: "franchise" })
      });
      const data = await res.json();
      setFranchiseDocs(data.documents || []);
    } catch { setFranchiseDocs([]); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.franchise_code || !formData.franchise_name) {
      toast.error("Franchise code and name are required");
      return;
    }
    
    setLoading(true);
    try {
      const endpoint = editingFranchise 
        ? `${API}/api/franchises/update/${editingFranchise.franchise_code}`
        : `${API}/api/franchises/create`;
      
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formData, token: session?.token })
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Failed to save franchise");
      }
      
      toast.success(editingFranchise ? "Franchise updated" : "Franchise created");
      setShowForm(false);
      setEditingFranchise(null);
      setFormData(getEmptyForm());
      loadFranchises();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (franchise) => {
    setEditingFranchise(franchise);
    // Use `??` (nullish-coalesce) not `||` so a stored numeric 0 is
    // preserved instead of being silently reset to the type default.
    // Ref: audit 2026-02 (Kalyan WC=0 was silently overwritten by ₹9L).
    setFormData({
      ...franchise,
      directors: franchise.directors?.length > 0 ? franchise.directors : [{ name: "", email: "", phone: "", designation: "Director", address: "", pan: "" }],
      franchise_type: franchise.franchise_type || "Sanskriti",
      franchise_fee: franchise.franchise_fee ?? FRANCHISE_TYPES[franchise.franchise_type || "Sanskriti"]?.fee ?? 0,
      working_capital: franchise.working_capital ?? DEFAULT_WORKING_CAPITAL,
      total_investment: franchise.total_investment ?? 0,
      setup_costs: franchise.setup_costs || { shop_security_deposit: 0, first_month_rent: 0, initial_salary_fund: 0, initial_grocery_cost: 0, staff_traveling_expense: 0 },
      revenue_share_start_date: franchise.revenue_share_start_date || franchise.operations_start_date || "",
      revenue_share_percentage: franchise.franchise_owner_share_percentage ?? franchise.revenue_share_percentage ?? REVENUE_SHARE_PERCENTAGE,
      franchise_owner_share_percentage: franchise.franchise_owner_share_percentage ?? franchise.revenue_share_percentage ?? REVENUE_SHARE_PERCENTAGE,
      // Payout model: explicit field wins; otherwise default by country.
      payout_model: franchise.payout_model || (franchise.country === "India" ? "revenue_share" : "profit_share"),
      service_contract_fee: franchise.service_contract_fee ?? MONTHLY_SERVICE_CONTRACT,
      nominees: franchise.nominees || [],
      gst_applicable: franchise.gst_applicable === true,
      // Legacy franchises without this field default to MG ON.
      mg_calculation_applicable: franchise.mg_calculation_applicable !== false,
      // Explicit legal_entity_name pass-through so a missing/undefined
      // value doesn't leave the controlled input uncontrolled.
      legal_entity_name: franchise.legal_entity_name ?? "",
      notes: franchise.notes ?? "",
      status: franchise.status || "Active",
      // Investment & Return Projection fields
      actual_investment_amount: franchise.actual_investment_amount ?? 0,
      investment_currency: franchise.investment_currency ?? "",
      investment_date: franchise.investment_date ?? "",
      additional_capital_introduced: franchise.additional_capital_introduced ?? 0,
      investment_working_capital: franchise.investment_working_capital ?? 0,
      other_capital_injection: franchise.other_capital_injection ?? 0,
      investment_notes: franchise.investment_notes ?? "",
      investment_verified_by: franchise.investment_verified_by ?? "",
      investment_verified_at: franchise.investment_verified_at ?? "",
    });
    setShowForm(true);
  };

  const handleDelete = async (franchise) => {
    if (!window.confirm(`Are you sure you want to delete franchise "${franchise.franchise_code}"?`)) {
      return;
    }
    
    try {
      const res = await fetch(`${API}/api/franchises/delete/${franchise.franchise_code}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: session?.token })
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Failed to delete");
      }
      
      toast.success("Franchise deleted");
      loadFranchises();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const loadFranchiseDetails = async (code) => {
    try {
      const res = await fetch(`${API}/api/franchises/get/${code}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: session?.token })
      });
      
      if (!res.ok) throw new Error("Failed to load details");
      
      const data = await res.json();
      setSelectedFranchise(data.franchise);
      setAuditHistory(data.audit_history || []);
      setActiveTab("detail");
      // Load documents from new Document Management system
      loadFranchiseDocs(code);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDocumentUpload = async (e) => {
    e.preventDefault();
    
    if (!uploadFile || !uploadData.document_type || !uploadData.document_name) {
      toast.error("Please fill all required fields and select a file");
      return;
    }
    
    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append("token", session?.token);
      // Tag the document with the FRANCHISE's home center, not the uploader's
      // center. Selecting a franchise from the management page implies the doc
      // belongs to that franchise's operating center (e.g., FR-004 → PB-DV),
      // even when uploaded by Super Admin sitting on PB-MGT.
      const franchiseCenter =
        selectedFranchise?.center_code
        || selectedFranchise?.home_center
        || (Array.isArray(selectedFranchise?.centers) && selectedFranchise.centers[0])
        || session?.center
        || "PB-MGT";
      formDataUpload.append("center", franchiseCenter);
      formDataUpload.append("category_id", uploadData.document_type);
      formDataUpload.append("level", "franchise");
      formDataUpload.append("franchise_code", selectedFranchise.franchise_code);
      formDataUpload.append("notes", uploadData.document_name + (uploadData.notes ? " - " + uploadData.notes : ""));
      formDataUpload.append("expiry_date", uploadData.expiry_date || "");
      formDataUpload.append("file", uploadFile);
      
      const res = await fetch(`${API}/api/documents/upload`, {
        method: "POST",
        body: formDataUpload
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Upload failed");
      }
      
      toast.success("Document uploaded");
      setShowUpload(false);
      setUploadFile(null);
      setUploadData({ document_type: "", document_name: "", notes: "", expiry_date: "" });
      loadFranchiseDocs(selectedFranchise.franchise_code);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDocumentDelete = async (docId) => {
    if (!window.confirm("Delete this document?")) return;
    
    try {
      const res = await fetch(`${API}/api/documents/delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: session?.token, document_id: docId })
      });
      
      if (!res.ok) throw new Error("Delete failed");
      
      toast.success("Document deleted");
      loadFranchiseDocs(selectedFranchise.franchise_code);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const downloadDocument = async (doc) => {
    try {
      const res = await fetch(`${API}/api/documents/file/${doc.document_id}?auth=${session?.token}`);
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_filename || "document";
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const generateAgreement = async () => {
    try {
      toast.loading("Generating FOCO Agreement...", { id: "gen-agreement" });
      const res = await fetch(`${API}/api/franchises/generate-agreement/${selectedFranchise.franchise_code}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: session?.token, format: "pdf" })
      });
      
      if (!res.ok) throw new Error("Failed to generate agreement");
      
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `FOCO_Agreement_${selectedFranchise.franchise_code}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success("Agreement generated", { id: "gen-agreement" });
    } catch (err) {
      toast.error(err.message, { id: "gen-agreement" });
    }
  };

  const addDirector = () => {
    setFormData(prev => ({
      ...prev,
      directors: [...prev.directors, { name: "", email: "", phone: "", designation: "Director", address: "", pan: "" }]
    }));
  };

  const removeDirector = (index) => {
    setFormData(prev => ({
      ...prev,
      directors: prev.directors.filter((_, i) => i !== index)
    }));
  };

  const updateDirector = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      directors: prev.directors.map((d, i) => i === index ? { ...d, [field]: value } : d)
    }));
  };

  // Access check
  const isSuperAdmin = session?.is_super_admin;
  const isAdmin = session?.is_admin;
  const hasAccounting = session?.roles?.accounting;
  
  if (!isSuperAdmin && !isAdmin && !hasAccounting) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <AlertCircle className="w-16 h-16 text-red-500 mb-4" />
        <h2 className="text-xl font-bold text-[#3b0d0d] mb-2">Access Denied</h2>
        <p className="text-muted-foreground">Only Admin or Accounts users can access Franchise Management.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="franchise-management">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#3b0d0d] flex items-center gap-2">
            <Building2 className="w-6 h-6 text-secondary" />
            Franchise Management
          </h1>
          <p className="text-muted-foreground mt-1">Manage franchise records, documents, and agreements</p>
        </div>
        <Button 
          onClick={() => { setShowForm(true); setEditingFranchise(null); setFormData(getEmptyForm()); }}
          className="bg-secondary hover:bg-secondary/90"
          data-testid="add-franchise-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Franchise
        </Button>
      </div>

      <FranchiseTiles franchises={franchises} counts={counts} />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-card border border-border">
          <TabsTrigger value="list" data-testid="tab-list">Franchise List</TabsTrigger>
          {selectedFranchise && <TabsTrigger value="detail" data-testid="tab-detail">Details</TabsTrigger>}
        </TabsList>

        {/* List Tab */}
        <TabsContent value="list" className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by code, name, city..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-[#fffdf8] border-[#D9AE52]"
                  data-testid="search-input"
                />
              </div>
            </div>
            <Select value={filterCountry} onValueChange={setFilterCountry}>
              <SelectTrigger className="w-[150px] bg-[#fffdf8] border-[#D9AE52]">
                <SelectValue placeholder="Country" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Countries</SelectItem>
                {COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[130px] bg-[#fffdf8] border-[#D9AE52]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={loadFranchises} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Refresh"}
            </Button>
          </div>

          {/* Table */}
          <Card className="bg-[#fffdf8] border-[#D9AE52]">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead>Code</TableHead>
                    <TableHead>Franchise Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Country</TableHead>
                    <TableHead>Fee</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto text-secondary" />
                      </TableCell>
                    </TableRow>
                  ) : franchises.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No franchises found. Click "Add Franchise" to create one.
                      </TableCell>
                    </TableRow>
                  ) : (
                    franchises.map((f) => (
                      <TableRow 
                        key={f.franchise_code} 
                        className="border-border cursor-pointer hover:bg-white/5"
                        onClick={() => loadFranchiseDetails(f.franchise_code)}
                        data-testid={`franchise-row-${f.franchise_code}`}
                      >
                        <TableCell className="font-mono font-semibold text-secondary">
                          {f.franchise_code}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium text-[#3b0d0d]">{f.franchise_name}</p>
                            <p className="text-xs text-muted-foreground">{f.city}, {f.country}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {f.franchise_type || "Sanskriti"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Globe className="w-3 h-3 text-muted-foreground" />
                            {f.country}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">
                          {formatCurrency(f.franchise_fee, f.country)}
                        </TableCell>
                        <TableCell>
                          <Badge className={statusColors[f.status] || statusColors.Inactive}>
                            {f.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button 
                              size="icon" 
                              variant="ghost"
                              onClick={(e) => { e.stopPropagation(); handleEdit(f); }}
                              data-testid={`edit-btn-${f.franchise_code}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            {isSuperAdmin && (
                              <Button 
                                size="icon" 
                                variant="ghost"
                                className="text-red-500 hover:text-red-700"
                                onClick={(e) => { e.stopPropagation(); handleDelete(f); }}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Detail Tab */}
        <TabsContent value="detail" className="space-y-4">
          {selectedFranchise && (
            <>
              {/* Franchise Header */}
              <Card className="bg-[#fffdf8] border-[#D9AE52]">
                <CardHeader className="flex flex-row items-start justify-between">
                  <div>
                    <CardTitle className="text-xl text-[#3b0d0d] flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-secondary" />
                      {selectedFranchise.franchise_name}
                    </CardTitle>
                    <p className="text-muted-foreground mt-1">{selectedFranchise.legal_entity_name}</p>
                    <Badge className={`mt-2 ${statusColors[selectedFranchise.status]}`}>
                      {selectedFranchise.status}
                    </Badge>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => handleEdit(selectedFranchise)}>
                      <Edit className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                    <Button onClick={generateAgreement} className="bg-secondary hover:bg-secondary/90">
                      <FileSignature className="w-4 h-4 mr-2" />
                      Generate Agreement
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Location */}
                    <div className="space-y-3">
                      <h3 className="font-semibold text-[#3b0d0d] flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-secondary" />
                        Location
                      </h3>
                      <div className="space-y-1 text-sm">
                        <p><span className="text-muted-foreground">Code:</span> <span className="font-mono text-secondary">{selectedFranchise.franchise_code}</span></p>
                        <p><span className="text-muted-foreground">Country:</span> {selectedFranchise.country}</p>
                        <p><span className="text-muted-foreground">State:</span> {selectedFranchise.state || "-"}</p>
                        <p><span className="text-muted-foreground">City:</span> {selectedFranchise.city || "-"}</p>
                        <p><span className="text-muted-foreground">Address:</span> {selectedFranchise.address || "-"}</p>
                        <p><span className="text-muted-foreground">Pincode:</span> {selectedFranchise.pincode || "-"}</p>
                      </div>
                    </div>

                    {/* Contact */}
                    <div className="space-y-3">
                      <h3 className="font-semibold text-[#3b0d0d] flex items-center gap-2">
                        <Phone className="w-4 h-4 text-secondary" />
                        Primary Contact
                      </h3>
                      <div className="space-y-1 text-sm">
                        <p><span className="text-muted-foreground">Name:</span> {selectedFranchise.primary_contact_name || "-"}</p>
                        <p className="flex items-center gap-1">
                          <Mail className="w-3 h-3 text-muted-foreground" />
                          {selectedFranchise.primary_contact_email || "-"}
                        </p>
                        <p className="flex items-center gap-1">
                          <Phone className="w-3 h-3 text-muted-foreground" />
                          {selectedFranchise.primary_contact_phone || "-"}
                        </p>
                      </div>
                    </div>

                    {/* Agreement */}
                    <div className="space-y-3">
                      <h3 className="font-semibold text-[#3b0d0d] flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-secondary" />
                        FOCO Agreement
                      </h3>
                      <div className="space-y-1 text-sm">
                        <p><span className="text-muted-foreground">Type:</span> <span className="text-secondary font-medium">{selectedFranchise.franchise_type || "Sanskriti"}</span></p>
                        <p data-testid="franchise-agreement-date-row">
                          <span className="text-muted-foreground">Franchise Agreement Date:</span>{" "}
                          <span className="text-secondary font-medium">
                            {selectedFranchise.franchise_agreement_date
                              ? selectedFranchise.franchise_agreement_date
                              : (selectedFranchise.agreement_start_date || "—")}
                          </span>
                          {selectedFranchise.franchise_agreement_date_source === "SA_CORRECTION" && (
                            <span className="ml-2 px-1.5 py-0.5 rounded-full border border-amber-400 bg-amber-50 text-amber-800 text-[10px]">SA Corrected</span>
                          )}
                          {session?.is_super_admin && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="ml-2 h-6 px-2 text-xs text-amber-700 hover:text-amber-100"
                              onClick={() => { resetAgreementCorrectionForm(); setShowAgreementCorrection(true); }}
                              data-testid="btn-correct-agreement-date"
                            >
                              Correct
                            </Button>
                          )}
                        </p>
                        <p><span className="text-muted-foreground">Start:</span> {selectedFranchise.operations_start_date || selectedFranchise.agreement_start_date || "-"}</p>
                        <p><span className="text-muted-foreground">End:</span> {selectedFranchise.agreement_end_date || "-"} <span className="text-xs text-muted-foreground">(7 years)</span></p>
                        <p className="flex items-center gap-1">
                          <DollarSign className="w-3 h-3 text-muted-foreground" />
                          Fee: {formatCurrency(selectedFranchise.franchise_fee, selectedFranchise.country)}
                        </p>
                        <p className="flex items-center gap-1">
                          <Percent className="w-3 h-3 text-muted-foreground" />
                          Franchise Owner Share: {selectedFranchise.franchise_owner_share_percentage ?? selectedFranchise.revenue_share_percentage ?? 15}%
                          <span className="text-xs text-muted-foreground">
                            (Company: {(100 - (parseFloat(selectedFranchise.franchise_owner_share_percentage ?? selectedFranchise.revenue_share_percentage ?? 15) || 0)).toFixed(1)}%)
                          </span>
                        </p>
                        <p data-testid="franchise-payout-model">
                          <span className="text-muted-foreground">Payout Model:</span>{" "}
                          {(selectedFranchise.payout_model || (selectedFranchise.country === "India" ? "revenue_share" : "profit_share")) === "profit_share" ? (
                            <span className="text-blue-700 font-medium">Profit Share</span>
                          ) : (
                            <span className="text-emerald-700 font-medium">Revenue Share</span>
                          )}
                        </p>
                        <p><span className="text-muted-foreground">Working Capital:</span> {formatCurrency(selectedFranchise.working_capital, selectedFranchise.country)}</p>
                        <p><span className="text-muted-foreground">Service Fee:</span> {formatCurrency(selectedFranchise.service_contract_fee || 10000, selectedFranchise.country)}/month</p>
                        <p data-testid="franchise-mg-status">
                          <span className="text-muted-foreground">MG Calculation:</span>{" "}
                          {selectedFranchise.mg_calculation_applicable !== false ? (
                            <span className="text-amber-700 font-medium">Applicable (MG vs Revenue Share)</span>
                          ) : (
                            <span className="text-emerald-700 font-medium">Not Applicable (Revenue Share only)</span>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* FOCO Model Info Banner */}
                  <div className="mt-4 p-3 rounded-lg bg-secondary/10 border border-secondary/30">
                    <p className="text-sm text-secondary font-medium">FOCO Model: Franchise Owned - Company Operated</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Franchise Owner invests capital. Purnabramha manages all operations including menu, staff, procurement, and accounting.
                    </p>
                  </div>

                  {/* Directors */}
                  {selectedFranchise.directors?.length > 0 && (
                    <div className="mt-6">
                      <h3 className="font-semibold text-[#3b0d0d] flex items-center gap-2 mb-3">
                        <Users className="w-4 h-4 text-secondary" />
                        Directors
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {selectedFranchise.directors.map((d, i) => (
                          <div key={i} className="p-3 rounded-lg bg-white/5 border border-border">
                            <p className="font-medium text-[#3b0d0d]">{d.name || "N/A"}</p>
                            <p className="text-sm text-muted-foreground">{d.designation || "Director"}</p>
                            <p className="text-sm">{d.email}</p>
                            <p className="text-sm">{d.phone}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Notes */}
                  {selectedFranchise.notes && (
                    <div className="mt-6">
                      <h3 className="font-semibold text-[#3b0d0d] mb-2">Notes</h3>
                      <p className="text-sm text-muted-foreground">{selectedFranchise.notes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Documents Section */}
              <Card className="bg-[#fffdf8] border-[#D9AE52]">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg text-[#3b0d0d] flex items-center gap-2">
                    <FileText className="w-5 h-5 text-secondary" />
                    Documents
                  </CardTitle>
                  <Button onClick={() => setShowUpload(true)} size="sm" data-testid="upload-doc-btn">
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Document
                  </Button>
                </CardHeader>
                <CardContent>
                  {franchiseDocs.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow className="border-border">
                          <TableHead>Name</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Uploaded</TableHead>
                          <TableHead>By</TableHead>
                          <TableHead>Expiry</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {franchiseDocs.map((doc) => {
                          const isExpired = doc.expiry_date && new Date(doc.expiry_date) < new Date();
                          const isExpiringSoon = doc.expiry_date && !isExpired && (new Date(doc.expiry_date) - new Date()) / (1000*60*60*24) <= 30;
                          return (
                          <TableRow key={doc.document_id} className="border-border" data-testid={`doc-row-${doc.document_id}`}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{doc.notes || doc.original_filename}</p>
                                <p className="text-xs text-muted-foreground">{doc.original_filename}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{doc.category_name || "Other"}</Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className={
                                doc.status === "approved" ? "bg-green-500/20 text-emerald-700 border-green-500/30" :
                                doc.status === "rejected" ? "bg-red-500/20 text-red-700 border-red-500/30" :
                                "bg-yellow-500/20 text-amber-700 border-yellow-500/30"
                              }>{doc.status}</Badge>
                            </TableCell>
                            <TableCell className="text-sm">
                              {doc.created_at ? new Date(doc.created_at).toLocaleDateString() : "-"}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {doc.uploaded_by}
                            </TableCell>
                            <TableCell className="text-sm">
                              {doc.expiry_date ? (
                                <span className={isExpired ? "text-red-500 font-semibold" : isExpiringSoon ? "text-orange-400 font-semibold" : ""}>
                                  {new Date(doc.expiry_date).toLocaleDateString()}
                                  {isExpired && " (Expired)"}
                                </span>
                              ) : "-"}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button 
                                  size="icon" 
                                  variant="ghost"
                                  onClick={() => downloadDocument(doc)}
                                  data-testid={`download-doc-${doc.document_id}`}
                                >
                                  <Download className="w-4 h-4" />
                                </Button>
                                <Button 
                                  size="icon" 
                                  variant="ghost"
                                  className="text-red-500"
                                  onClick={() => handleDocumentDelete(doc.document_id)}
                                  data-testid={`delete-doc-${doc.document_id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-center text-muted-foreground py-8">No documents uploaded yet.</p>
                  )}
                </CardContent>
              </Card>

              {/* Audit History */}
              <Card className="bg-[#fffdf8] border-[#D9AE52]">
                <CardHeader>
                  <CardTitle className="text-lg text-[#3b0d0d] flex items-center gap-2">
                    <History className="w-5 h-5 text-secondary" />
                    Audit History
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {auditHistory.length > 0 ? (
                    <div className="space-y-3 max-h-[300px] overflow-y-auto">
                      {auditHistory.map((entry, i) => (
                        <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-white/5">
                          <div className="w-2 h-2 rounded-full bg-secondary mt-2"></div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <p className="font-medium text-[#3b0d0d]">{entry.action}</p>
                              <p className="text-xs text-muted-foreground">
                                {new Date(entry.timestamp).toLocaleString()}
                              </p>
                            </div>
                            <p className="text-sm text-muted-foreground">by {entry.performed_by}</p>
                            {entry.details && (
                              <pre className="text-xs mt-1 text-muted-foreground overflow-x-auto">
                                {JSON.stringify(entry.details, null, 2)}
                              </pre>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-4">No audit history available.</p>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-[#fffdf8] border-[#D9AE52]">
          <DialogHeader>
            <DialogTitle className="text-xl text-slate-900">
              {editingFranchise ? "Edit Franchise" : "Add New Franchise"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-900 border-b border-slate-200 pb-2">Basic Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Franchise Code *</Label>
                  <Input
                    value={formData.franchise_code}
                    onChange={(e) => setFormData(p => ({ ...p, franchise_code: e.target.value.toUpperCase() }))}
                    placeholder="FR-001"
                    disabled={!!editingFranchise}
                    className="bg-background"
                    data-testid="input-franchise-code"
                  />
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={formData.status} onValueChange={(v) => setFormData(p => ({ ...p, status: v }))}>
                    <SelectTrigger className="bg-background">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Franchise Name *</Label>
                  <Input
                    value={formData.franchise_name}
                    onChange={(e) => setFormData(p => ({ ...p, franchise_name: e.target.value }))}
                    placeholder="Purnabramha Perth"
                    className="bg-background"
                    data-testid="input-franchise-name"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Legal Entity Name</Label>
                  <Input
                    value={formData.legal_entity_name}
                    onChange={(e) => setFormData(p => ({ ...p, legal_entity_name: e.target.value }))}
                    placeholder="ABC Foods Pvt Ltd"
                    className="bg-background"
                  />
                </div>
              </div>
            </div>

            {/* Location */}
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-900 border-b border-slate-200 pb-2">Location</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Country</Label>
                  <Select value={formData.country} onValueChange={(v) => setFormData(p => ({ ...p, country: v }))}>
                    <SelectTrigger className="bg-background">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>State/Province</Label>
                  <Input
                    value={formData.state}
                    onChange={(e) => setFormData(p => ({ ...p, state: e.target.value }))}
                    className="bg-background"
                  />
                </div>
                <div>
                  <Label>City</Label>
                  <Input
                    value={formData.city}
                    onChange={(e) => setFormData(p => ({ ...p, city: e.target.value }))}
                    className="bg-background"
                  />
                </div>
                <div>
                  <Label>Pincode/ZIP</Label>
                  <Input
                    value={formData.pincode}
                    onChange={(e) => setFormData(p => ({ ...p, pincode: e.target.value }))}
                    className="bg-background"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Address</Label>
                  <Textarea
                    value={formData.address}
                    onChange={(e) => setFormData(p => ({ ...p, address: e.target.value }))}
                    className="bg-background"
                    rows={2}
                  />
                </div>
              </div>
            </div>

            {/* Contact */}
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-900 border-b border-slate-200 pb-2">Primary Contact</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Contact Name</Label>
                  <Input
                    value={formData.primary_contact_name}
                    onChange={(e) => setFormData(p => ({ ...p, primary_contact_name: e.target.value }))}
                    className="bg-background"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.primary_contact_email}
                    onChange={(e) => setFormData(p => ({ ...p, primary_contact_email: e.target.value }))}
                    className="bg-background"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={formData.primary_contact_phone}
                    onChange={(e) => setFormData(p => ({ ...p, primary_contact_phone: e.target.value }))}
                    className="bg-background"
                  />
                </div>
              </div>
            </div>

            {/* Directors */}
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b border-border pb-2">
                <h3 className="font-semibold text-slate-900">Directors</h3>
                <Button type="button" variant="outline" size="sm" onClick={addDirector}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Director
                </Button>
              </div>
              {formData.directors.map((d, i) => (
                <div key={i} className="p-4 rounded-lg bg-white/5 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-muted-foreground">Director {i + 1}</span>
                    {formData.directors.length > 1 && (
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeDirector(i)}>
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      placeholder="Name"
                      value={d.name}
                      onChange={(e) => updateDirector(i, "name", e.target.value)}
                      className="bg-background"
                    />
                    <Input
                      placeholder="Designation"
                      value={d.designation}
                      onChange={(e) => updateDirector(i, "designation", e.target.value)}
                      className="bg-background"
                    />
                    <Input
                      placeholder="Email"
                      value={d.email}
                      onChange={(e) => updateDirector(i, "email", e.target.value)}
                      className="bg-background"
                    />
                    <Input
                      placeholder="Phone"
                      value={d.phone}
                      onChange={(e) => updateDirector(i, "phone", e.target.value)}
                      className="bg-background"
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* FOCO Model - Franchise Type */}
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-900 border-b border-slate-200 pb-2">FOCO Model - Franchise Type</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Franchise Type *</Label>
                  <Select value={formData.franchise_type} onValueChange={handleFranchiseTypeChange}>
                    <SelectTrigger className="bg-background">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(FRANCHISE_TYPES).map(([type, info]) => (
                        <SelectItem key={type} value={type}>
                          {type} - {formatCurrency(info.fee, formData.country)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    {FRANCHISE_TYPES[formData.franchise_type]?.description}
                  </p>
                </div>
                <div>
                  <Label>Franchise Fee (Non-Refundable)</Label>
                  <Input
                    type="number"
                    value={formData.franchise_fee}
                    onChange={(e) => setFormData(p => ({ ...p, franchise_fee: parseFloat(e.target.value) || 0 }))}
                    className="bg-background"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatCurrency(formData.franchise_fee, formData.country)}
                  </p>
                </div>
                <div>
                  <Label>Working Capital</Label>
                  <Input
                    type="number"
                    value={formData.working_capital}
                    onChange={(e) => setFormData(p => ({ ...p, working_capital: parseFloat(e.target.value) || 0 }))}
                    className="bg-background"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Default: {formatCurrency(DEFAULT_WORKING_CAPITAL, formData.country)}
                  </p>
                </div>
                <div>
                  <Label>Franchise Owner Share %</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.franchise_owner_share_percentage ?? formData.revenue_share_percentage}
                    onChange={(e) => {
                      const v = parseFloat(e.target.value) || 0;
                      // Keep both fields in sync for backwards compatibility.
                      setFormData(p => ({
                        ...p,
                        franchise_owner_share_percentage: v,
                        revenue_share_percentage: v,
                      }));
                    }}
                    className="bg-background"
                    data-testid="franchise-owner-share-pct-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Company Share auto = <span className="font-semibold text-foreground">{(100 - (parseFloat(formData.franchise_owner_share_percentage ?? formData.revenue_share_percentage) || 0)).toFixed(1)}%</span>
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                <div>
                  <Label>Payout Model</Label>
                  <Select
                    value={formData.payout_model || (formData.country === "India" ? "revenue_share" : "profit_share")}
                    onValueChange={(v) => setFormData(p => ({ ...p, payout_model: v }))}
                  >
                    <SelectTrigger className="bg-background" data-testid="payout-model-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="revenue_share">Revenue Share</SelectItem>
                      <SelectItem value="profit_share">Profit Share</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    {formData.payout_model === "profit_share"
                      ? <>Owner share = <b>Profit Share Base × {(formData.franchise_owner_share_percentage ?? formData.revenue_share_percentage) || 0}%</b> (Sales − Comm − Expenses − Adjustments)</>
                      : <>Owner share = <b>Revenue Share Base × {(formData.franchise_owner_share_percentage ?? formData.revenue_share_percentage) || 0}%</b> (Sales − Comm − GST)</>}
                  </p>
                </div>
              </div>
            </div>

            {/* MG Calculation Toggle — gates the entire Total Investment / MG block.
                When OFF, payouts use Revenue Share % × Revenue Share Base only. */}
            <div className="space-y-3">
              <h3 className="font-semibold text-slate-900 border-b border-slate-200 pb-2">Payout Model</h3>
              {formData.mg_calculation_applicable !== false ? (
                <label
                  className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-300 rounded-lg cursor-pointer hover:bg-amber-100 transition-colors"
                  data-testid="mg-applicable-toggle-label"
                >
                  <input
                    type="checkbox"
                    checked={true}
                    onChange={(e) => setFormData(p => ({ ...p, mg_calculation_applicable: e.target.checked }))}
                    className="mt-1 h-4 w-4 rounded border-amber-500 text-amber-600 focus:ring-amber-500 cursor-pointer"
                    data-testid="mg-applicable-checkbox"
                  />
                  <div className="flex-1">
                    <div className="font-semibold text-amber-900">MG Calculation Applicable</div>
                    <p className="text-sm text-amber-800 mt-1">
                      <b>ON</b> — Payout is the higher of MG vs Revenue Share. Total Investment &amp; MG deductions are required (below).
                    </p>
                  </div>
                </label>
              ) : (
                <label
                  className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-300 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors"
                  data-testid="mg-applicable-toggle-label"
                >
                  <input
                    type="checkbox"
                    checked={false}
                    onChange={(e) => setFormData(p => ({ ...p, mg_calculation_applicable: e.target.checked }))}
                    className="mt-1 h-4 w-4 rounded border-blue-500 text-blue-600 focus:ring-blue-500 cursor-pointer"
                    data-testid="mg-applicable-checkbox"
                  />
                  <div className="flex-1">
                    <div className="font-semibold text-blue-900 flex items-center gap-1.5">
                      <Info className="w-4 h-4" />MG Calculation Not Applicable
                    </div>
                    <p className="text-sm text-blue-800 mt-1">
                      MG is <b>OFF</b> — this payout uses Revenue Share % × Revenue Share Base only. No MG calculation and no MG comparison will be performed.
                    </p>
                    <p className="text-xs text-blue-700 mt-2">Tick the box to enable MG Calculation.</p>
                  </div>
                </label>
              )}
            </div>

            {/* Total Investment for MG Calculation — only shown when MG is applicable */}
            {formData.mg_calculation_applicable !== false && (
            <div className="space-y-4" data-testid="total-investment-block">
              <h3 className="font-semibold text-slate-900 border-b border-slate-200 pb-2">Total Investment (for MG Calculation)</h3>
              <div className="grid grid-cols-1 gap-4">
                <div className="p-4 bg-purple-900/30 border border-purple-500/30 rounded-lg">
                  <Label className="text-purple-800">Total Investment Done *</Label>
                  <Input
                    type="number"
                    value={formData.total_investment || 0}
                    onChange={(e) => setFormData(p => ({ ...p, total_investment: parseFloat(e.target.value) || 0 }))}
                    className="bg-background mt-2 text-lg font-bold"
                    placeholder="Enter total investment amount"
                    data-testid="total-investment-input"
                  />
                  <p className="text-xs text-purple-700 mt-2">
                    This is the actual total investment amount. All deductions below (including Franchise Fee & Working Capital) will be subtracted to calculate Net Investment for MG.
                  </p>
                  {formData.total_investment > 0 && (
                    <div className="mt-3 p-3 bg-background/50 rounded text-sm space-y-1">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Total Investment:</span>
                        <span className="font-medium">{formatCurrency(formData.total_investment, formData.country)}</span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-2 mb-1">Deductions:</div>
                      <div className="flex justify-between text-red-700 text-xs pl-2">
                        <span>- Shop Rent Deposit:</span>
                        <span>-{formatCurrency(formData.setup_costs?.shop_security_deposit || 0, formData.country)}</span>
                      </div>
                      <div className="flex justify-between text-red-700 text-xs pl-2">
                        <span>- First Month Rent:</span>
                        <span>-{formatCurrency(formData.setup_costs?.first_month_rent || 0, formData.country)}</span>
                      </div>
                      <div className="flex justify-between text-red-700 text-xs pl-2">
                        <span>- Initial Salary Fund:</span>
                        <span>-{formatCurrency(formData.setup_costs?.initial_salary_fund || 0, formData.country)}</span>
                      </div>
                      <div className="flex justify-between text-red-700 text-xs pl-2">
                        <span>- Staff Travel Expense:</span>
                        <span>-{formatCurrency(formData.setup_costs?.staff_traveling_expense || 0, formData.country)}</span>
                      </div>
                      <div className="flex justify-between text-red-700 text-xs pl-2">
                        <span>- Franchise Fee:</span>
                        <span>-{formatCurrency(formData.franchise_fee || 0, formData.country)}</span>
                      </div>
                      <div className="flex justify-between text-red-700 text-xs pl-2">
                        <span>- Working Capital:</span>
                        <span>-{formatCurrency(formData.working_capital || 0, formData.country)}</span>
                      </div>
                      <div className="flex justify-between text-red-700 font-medium border-t border-border/50 pt-1 mt-1">
                        <span>Total Deductions:</span>
                        <span>-{formatCurrency(
                          (formData.setup_costs?.shop_security_deposit || 0) +
                          (formData.setup_costs?.first_month_rent || 0) +
                          (formData.setup_costs?.initial_salary_fund || 0) +
                          (formData.setup_costs?.staff_traveling_expense || 0) +
                          (formData.franchise_fee || 0) +
                          (formData.working_capital || 0),
                          formData.country
                        )}</span>
                      </div>
                      <div className="flex justify-between font-bold text-emerald-700 border-t-2 border-green-500/50 pt-2 mt-2 text-base">
                        <span>Net Investment:</span>
                        <span>{formatCurrency(
                          Math.max(0, (formData.total_investment || 0) -
                            (formData.setup_costs?.shop_security_deposit || 0) -
                            (formData.setup_costs?.first_month_rent || 0) -
                            (formData.setup_costs?.initial_salary_fund || 0) -
                            (formData.setup_costs?.staff_traveling_expense || 0) -
                            (formData.franchise_fee || 0) -
                            (formData.working_capital || 0)),
                          formData.country
                        )}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            )}

            {/* Actual Investment (Investment & Return Projection Engine) */}
            <div className="space-y-4" data-testid="actual-investment-block">
              <h3 className="font-semibold text-slate-900 border-b border-slate-200 pb-2">
                Actual Investment (for Return Projection)
              </h3>
              <p className="text-sm text-slate-700 -mt-2 leading-relaxed">
                <strong className="text-slate-900">This is the Franchise Owner's Actual Capital Deployed.</strong>{" "}
                It is a separate value from Franchise Fee and operational Working Capital and drives the
                Investment &amp; Return Projection engine.
              </p>
              <div className="grid grid-cols-1 gap-4">
                <div className="p-5 bg-emerald-50 border border-emerald-300 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-emerald-800 font-semibold text-sm">Actual Investment Amount <span className="text-red-600">*</span></Label>
                      <div className="relative mt-1.5">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm font-medium pointer-events-none">
                          {(formData.investment_currency === "AUD" && "A$") ||
                           (formData.investment_currency === "GBP" && "£") ||
                           (formData.investment_currency === "USD" && "$") ||
                           "₹"}
                        </span>
                        <Input
                          type="number"
                          value={formData.actual_investment_amount || 0}
                          onChange={(e) => setFormData(p => ({ ...p, actual_investment_amount: parseFloat(e.target.value) || 0 }))}
                          className="bg-white pl-8 text-lg font-bold text-slate-900 border-slate-300 focus:border-emerald-500 focus:ring-emerald-500"
                          placeholder="0.00"
                          data-testid="actual-investment-input"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-emerald-800 font-semibold text-sm">Currency <span className="text-red-600">*</span></Label>
                      <Select
                        value={formData.investment_currency || ""}
                        onValueChange={(v) => setFormData(p => ({ ...p, investment_currency: v }))}
                      >
                        <SelectTrigger className="bg-white mt-1.5 text-slate-900 border-slate-300 focus:border-emerald-500" data-testid="investment-currency-select">
                          <SelectValue placeholder="Select currency" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="AUD">AUD — Australian Dollar</SelectItem>
                          <SelectItem value="INR">INR — Indian Rupee</SelectItem>
                          <SelectItem value="GBP">GBP — British Pound</SelectItem>
                          <SelectItem value="USD">USD — US Dollar</SelectItem>
                          <SelectItem value="AED">AED — UAE Dirham</SelectItem>
                          <SelectItem value="SGD">SGD — Singapore Dollar</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-emerald-800 font-semibold text-sm">Investment Date / Confirmed Date <span className="text-red-600">*</span></Label>
                      <Input
                        type="date"
                        value={formData.investment_date || ""}
                        onChange={(e) => setFormData(p => ({ ...p, investment_date: e.target.value }))}
                        className="bg-white mt-1.5 text-slate-900 border-slate-300 focus:border-emerald-500 focus:ring-emerald-500"
                        data-testid="investment-date-input"
                      />
                    </div>
                    <div>
                      <Label className="text-emerald-800 font-semibold text-sm">Investment Working Capital</Label>
                      <Input
                        type="number"
                        value={formData.investment_working_capital || 0}
                        onChange={(e) => setFormData(p => ({ ...p, investment_working_capital: parseFloat(e.target.value) || 0 }))}
                        className="bg-white mt-1.5 text-slate-900 border-slate-300 focus:border-emerald-500 focus:ring-emerald-500"
                        placeholder="Capital-side WC (separate from operational WC)"
                        data-testid="investment-wc-input"
                      />
                    </div>
                    <div>
                      <Label className="text-emerald-800 font-semibold text-sm">Additional Capital Introduced</Label>
                      <Input
                        type="number"
                        value={formData.additional_capital_introduced || 0}
                        onChange={(e) => setFormData(p => ({ ...p, additional_capital_introduced: parseFloat(e.target.value) || 0 }))}
                        className="bg-white mt-1.5 text-slate-900 border-slate-300 focus:border-emerald-500 focus:ring-emerald-500"
                        placeholder="Top-ups after go-live"
                        data-testid="additional-capital-input"
                      />
                    </div>
                    <div>
                      <Label className="text-emerald-800 font-semibold text-sm">Other Approved Capital Injection</Label>
                      <Input
                        type="number"
                        value={formData.other_capital_injection || 0}
                        onChange={(e) => setFormData(p => ({ ...p, other_capital_injection: parseFloat(e.target.value) || 0 }))}
                        className="bg-white mt-1.5 text-slate-900 border-slate-300 focus:border-emerald-500 focus:ring-emerald-500"
                        placeholder="Loan / grant / other approved capital"
                        data-testid="other-capital-input"
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <Label className="text-slate-900 font-semibold text-sm">Investment Notes</Label>
                    <Input
                      value={formData.investment_notes || ""}
                      onChange={(e) => setFormData(p => ({ ...p, investment_notes: e.target.value }))}
                      className="bg-white mt-1.5 text-slate-900 border-slate-300 focus:border-emerald-500 focus:ring-emerald-500"
                      placeholder="e.g. Amount confirmed via bank statement, tranches, exchange rate…"
                      data-testid="investment-notes-input"
                    />
                  </div>

                  {/* Verification block */}
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-emerald-200">
                    <div>
                      <Label className="text-[#172554] font-semibold text-sm">Verified By</Label>
                      <Input
                        value={formData.investment_verified_by || ""}
                        onChange={(e) => setFormData(p => ({ ...p, investment_verified_by: e.target.value }))}
                        className="bg-white mt-1.5 text-slate-900 border-slate-300 focus:border-emerald-500 focus:ring-emerald-500"
                        placeholder="Authorised user name"
                        data-testid="investment-verified-by-input"
                      />
                    </div>
                    <div>
                      <Label className="text-[#172554] font-semibold text-sm">Verified At</Label>
                      <Input
                        type="date"
                        value={(formData.investment_verified_at || "").slice(0, 10)}
                        onChange={(e) => setFormData(p => ({ ...p, investment_verified_at: e.target.value }))}
                        className="bg-white mt-1.5 text-slate-900 border-slate-300 focus:border-emerald-500 focus:ring-emerald-500"
                        data-testid="investment-verified-at-input"
                      />
                    </div>
                  </div>

                  {(formData.actual_investment_amount || 0) > 0 && (
                    <div className="mt-4 p-3 bg-white/80 border border-emerald-200 rounded text-sm space-y-1">
                      <div className="flex justify-between text-slate-800">
                        <span>Actual Investment:</span>
                        <span className="font-semibold">{formatCurrency(formData.actual_investment_amount || 0, formData.country)}</span>
                      </div>
                      <div className="flex justify-between text-xs text-slate-700">
                        <span>+ Additional Capital:</span>
                        <span className="font-medium">{formatCurrency(formData.additional_capital_introduced || 0, formData.country)}</span>
                      </div>
                      <div className="flex justify-between text-xs text-slate-700">
                        <span>+ Investment WC:</span>
                        <span className="font-medium">{formatCurrency(formData.investment_working_capital || 0, formData.country)}</span>
                      </div>
                      <div className="flex justify-between text-xs text-slate-700">
                        <span>+ Other Injection:</span>
                        <span className="font-medium">{formatCurrency(formData.other_capital_injection || 0, formData.country)}</span>
                      </div>
                      <div className="flex justify-between font-bold text-emerald-800 border-t-2 border-emerald-500 pt-2 mt-2 text-base">
                        <span>Total Capital Deployed:</span>
                        <span data-testid="total-capital-deployed">
                          {formatCurrency(
                            (formData.actual_investment_amount || 0) +
                            (formData.additional_capital_introduced || 0) +
                            (formData.investment_working_capital || 0) +
                            (formData.other_capital_injection || 0),
                            formData.country
                          )}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Setup Costs */}
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-900 border-b border-slate-200 pb-2">Setup Costs (Before Operations)</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Shop Security Deposit</Label>
                  <Input
                    type="number"
                    value={formData.setup_costs?.shop_security_deposit || 0}
                    onChange={(e) => setFormData(p => ({ 
                      ...p, 
                      setup_costs: { ...p.setup_costs, shop_security_deposit: parseFloat(e.target.value) || 0 }
                    }))}
                    className="bg-background"
                  />
                </div>
                <div>
                  <Label>First Month Rent</Label>
                  <Input
                    type="number"
                    value={formData.setup_costs?.first_month_rent || 0}
                    onChange={(e) => setFormData(p => ({ 
                      ...p, 
                      setup_costs: { ...p.setup_costs, first_month_rent: parseFloat(e.target.value) || 0 }
                    }))}
                    className="bg-background"
                  />
                </div>
                <div>
                  <Label>Initial Salary Fund</Label>
                  <Input
                    type="number"
                    value={formData.setup_costs?.initial_salary_fund || 0}
                    onChange={(e) => setFormData(p => ({ 
                      ...p, 
                      setup_costs: { ...p.setup_costs, initial_salary_fund: parseFloat(e.target.value) || 0 }
                    }))}
                    className="bg-background"
                  />
                </div>
                <div>
                  <Label>Initial Grocery Cost</Label>
                  <Input
                    type="number"
                    value={formData.setup_costs?.initial_grocery_cost || 0}
                    onChange={(e) => setFormData(p => ({ 
                      ...p, 
                      setup_costs: { ...p.setup_costs, initial_grocery_cost: parseFloat(e.target.value) || 0 }
                    }))}
                    className="bg-background"
                  />
                </div>
                <div>
                  <Label>Staff Traveling Expense</Label>
                  <Input
                    type="number"
                    value={formData.setup_costs?.staff_traveling_expense || 0}
                    onChange={(e) => setFormData(p => ({ 
                      ...p, 
                      setup_costs: { ...p.setup_costs, staff_traveling_expense: parseFloat(e.target.value) || 0 }
                    }))}
                    className="bg-background"
                    data-testid="input-staff-travel"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Used for MG (Minimum Guarantee) calculation
                  </p>
                </div>
              </div>
            </div>

            {/* Agreement Dates */}
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-900 border-b border-slate-200 pb-2">Agreement Dates (Tenure: 7 Years Fixed)</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Operations Start Date</Label>
                  <Input
                    type="date"
                    value={formData.operations_start_date}
                    onChange={(e) => handleStartDateChange(e.target.value)}
                    className="bg-background"
                  />
                </div>
                <div>
                  <Label>Agreement End Date (Auto-calculated)</Label>
                  <Input
                    type="date"
                    value={formData.agreement_end_date}
                    disabled
                    className="bg-background opacity-70"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    7 years from start date (fixed tenure)
                  </p>
                </div>
                <div>
                  <Label>Revenue Share Start Date *</Label>
                  <Input
                    type="date"
                    value={formData.revenue_share_start_date || formData.operations_start_date}
                    onChange={(e) => setFormData(p => ({ ...p, revenue_share_start_date: e.target.value }))}
                    className="bg-background"
                    data-testid="revenue-share-start-date"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Date from which payout calculations start (defaults to Operations Start)
                  </p>
                </div>
                <div>
                  <Label>Monthly Service Contract Fee</Label>
                  <Input
                    type="number"
                    value={formData.service_contract_fee}
                    onChange={(e) => {
                      // Preserve explicit 0 — `|| 10000` silently reverted it to default.
                      const raw = e.target.value;
                      const v = raw === "" ? 0 : (Number.isFinite(parseFloat(raw)) ? parseFloat(raw) : 0);
                      setFormData(p => ({ ...p, service_contract_fee: v }));
                    }}
                    className="bg-background"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Default: {formatCurrency(MONTHLY_SERVICE_CONTRACT, formData.country)}/month
                  </p>
                </div>
              </div>
            </div>

            {/* GST Settings (India Only) */}
            {formData.country === "India" && (
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-900 border-b border-slate-200 pb-2">GST Settings (India Only)</h3>
                <div className="flex items-center justify-between p-4 rounded-lg bg-white/5 border border-border">
                  <div>
                    <Label className="text-base">GST Applicable on Revenue Share</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      When enabled, 18% GST (9% CGST + 9% SGST) will be applied to the revenue share payable.
                    </p>
                  </div>
                  <Switch
                    checked={formData.gst_applicable || false}
                    onCheckedChange={(checked) => setFormData(p => ({ ...p, gst_applicable: checked }))}
                    data-testid="gst-applicable-switch"
                  />
                </div>
              </div>
            )}

            {/* Notes */}
            <div>
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData(p => ({ ...p, notes: e.target.value }))}
                className="bg-background"
                rows={3}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading} className="bg-secondary hover:bg-secondary/90">
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingFranchise ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Document Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="bg-[#fffdf8] border-[#D9AE52]">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Upload Document</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleDocumentUpload} className="space-y-4">
            <div>
              <Label>Document Type *</Label>
              <Select 
                value={uploadData.document_type} 
                onValueChange={(v) => setUploadData(p => ({ ...p, document_type: v }))}
              >
                <SelectTrigger className="bg-background" data-testid="upload-doc-type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {docCategories.map(c => <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>)}
                  {docCategories.length === 0 && <SelectItem value="_none" disabled>No categories - create from Documents page</SelectItem>}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Document Name *</Label>
              <Input
                value={uploadData.document_name}
                onChange={(e) => setUploadData(p => ({ ...p, document_name: e.target.value }))}
                placeholder="e.g., Franchise Agreement 2024"
                className="bg-background"
                data-testid="upload-doc-name"
              />
            </div>
            <div>
              <Label>Expiry Date</Label>
              <Input
                type="date"
                value={uploadData.expiry_date}
                onChange={(e) => setUploadData(p => ({ ...p, expiry_date: e.target.value }))}
                className="bg-background"
                data-testid="upload-doc-expiry"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={uploadData.notes}
                onChange={(e) => setUploadData(p => ({ ...p, notes: e.target.value }))}
                className="bg-background"
                rows={2}
              />
            </div>
            <div>
              <Label>File *</Label>
              <Input
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx,.xls"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                className="bg-background"
                data-testid="upload-doc-file"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Allowed: PDF, DOC, DOCX, JPG, PNG, XLS, XLSX (Max 10MB)
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowUpload(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={uploading} data-testid="submit-upload-btn">
                {uploading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Upload
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* iter228 — Franchise Agreement Date SA correction */}
      <Dialog open={showAgreementCorrection} onOpenChange={(v) => { setShowAgreementCorrection(v); if (v) loadAgreementHistory(); }}>
        <DialogContent className="max-w-xl" data-testid="agreement-date-correction-modal">
          <DialogHeader>
            <DialogTitle className="text-amber-700">Correct Franchise Agreement Date</DialogTitle>
            <DialogDescription className="text-xs text-slate-700">
              Franchise Agreement Date is legal/master data.  Correction requires Super Admin authority, a documented reason, and a confirmation checkbox.  The previous value is preserved in the audit trail.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] text-slate-500">Current Date</Label>
                <div className="h-8 px-3 flex items-center border rounded bg-slate-50 text-xs" data-testid="agr-current-date">
                  {selectedFranchise?.franchise_agreement_date || selectedFranchise?.agreement_start_date || '—'}
                </div>
              </div>
              <div>
                <Label className="text-[11px] text-slate-500">Correct Date <span className="text-red-500">*</span></Label>
                <Input type="date" value={agrForm.new_date} onChange={(e) => setAgrForm({ ...agrForm, new_date: e.target.value })} data-testid="agr-new-date" />
              </div>
            </div>
            <div>
              <Label className="text-[11px] text-slate-500">Reason for Correction <span className="text-red-500">*</span></Label>
              <Select value={agrForm.reason_code || undefined} onValueChange={(v) => setAgrForm({ ...agrForm, reason_code: v })}>
                <SelectTrigger data-testid="agr-reason"><SelectValue placeholder="Select a reason" /></SelectTrigger>
                <SelectContent>
                  {agrReasonCodes.map(r => <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] text-slate-500">
                Remarks {agrForm.reason_code === 'OTHER' && <span className="text-red-500">* (mandatory when reason is Other)</span>}
              </Label>
              <Textarea rows={2} value={agrForm.remarks} onChange={(e) => setAgrForm({ ...agrForm, remarks: e.target.value })} data-testid="agr-remarks" />
            </div>
            <div>
              <Label className="text-[11px] text-slate-500">Supporting Document Reference (agreement file / document no.)</Label>
              <Input value={agrForm.supporting_reference} onChange={(e) => setAgrForm({ ...agrForm, supporting_reference: e.target.value })} data-testid="agr-support-ref" />
            </div>
            <label className="flex items-start gap-2 text-xs text-slate-700">
              <Checkbox checked={agrForm.confirmed} onCheckedChange={(v) => setAgrForm({ ...agrForm, confirmed: !!v })} data-testid="agr-confirm" />
              <span>I confirm this date has been verified against the signed franchise agreement.</span>
            </label>
            {agrHistory.length > 0 && (
              <div className="border rounded p-2 bg-slate-50 max-h-32 overflow-y-auto text-[11px]" data-testid="agr-history-list">
                <div className="font-semibold mb-1">Previous corrections:</div>
                {agrHistory.map((h, idx) => (
                  <div key={idx} className="border-b last:border-b-0 py-1">
                    <b>{h.old_value || '—'} → {h.new_value}</b> · {h.reason_label} · by {h.changed_by} on {(h.changed_at || '').slice(0, 19)}
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAgreementCorrection(false)}>Cancel</Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={submitAgreementCorrection}
              disabled={agrSubmitting}
              data-testid="agr-submit"
            >
              {agrSubmitting ? 'Saving…' : 'Save Corrected Date'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
